import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { TrendingUp, TrendingDown, Target, DollarSign, Users, Percent } from 'lucide-react';
interface KPIMetric {
  title: string;
  target: number;
  actual: number;
  unit: string;
  icon: any;
}
export function KPITracker() {
  const [metrics] = useState<KPIMetric[]>([{
    title: 'Monatlicher Umsatz',
    target: 50000,
    actual: 45000,
    unit: '€',
    icon: DollarSign
  }, {
    title: 'Quartalsumsatz',
    target: 150000,
    actual: 135000,
    unit: '€',
    icon: DollarSign
  }, {
    title: 'Verkaufsgespräche',
    target: 50,
    actual: 48,
    unit: '',
    icon: Users
  }, {
    title: 'Neue Leads',
    target: 100,
    actual: 95,
    unit: '',
    icon: Target
  }, {
    title: 'Lead-zu-Opportunity',
    target: 30,
    actual: 28,
    unit: '%',
    icon: Percent
  }, {
    title: 'Opportunity-zu-Abschluss',
    target: 25,
    actual: 22,
    unit: '%',
    icon: Percent
  }]);
  const calculateDeviation = (target: number, actual: number) => {
    const deviation = (actual - target) / target * 100;
    return deviation.toFixed(1);
  };
  const isPositive = (target: number, actual: number) => actual >= target;
  return <div className="p-6 space-y-6" data-id="element-196">
      <div data-id="element-197">
        <h1 className="text-3xl font-bold text-foreground" data-id="element-198">KPI Tracker</h1>
        <p className="text-muted-foreground mt-1" data-id="element-199">
          Wichtige Vertriebskennzahlen im Überblick
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-200">
        {metrics.map((metric, index) => {
        const deviation = calculateDeviation(metric.target, metric.actual);
        const positive = isPositive(metric.target, metric.actual);
        const Icon = metric.icon;
        return <Card key={index} data-id="element-201">
              <CardHeader data-id="element-202">
                <div className="flex items-start justify-between" data-id="element-203">
                  <div className="flex-1" data-id="element-204">
                    <CardTitle className="text-base" data-id="element-205">{metric.title}</CardTitle>
                  </div>
                  <div className="h-10 w-10 rounded-lg bg-[#eb6641]/10 flex items-center justify-center" data-id="element-206">
                    <Icon className="h-5 w-5 text-[#eb6641]" data-id="element-207" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3" data-id="element-208">
                <div className="space-y-2" data-id="element-209">
                  <div className="flex items-center justify-between text-sm" data-id="element-210">
                    <span className="text-muted-foreground" data-id="element-211">Ziel:</span>
                    <span className="font-medium" data-id="element-212">
                      {metric.target.toLocaleString()}
                      {metric.unit}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm" data-id="element-213">
                    <span className="text-muted-foreground" data-id="element-214">Ist:</span>
                    <span className="font-medium" data-id="element-215">
                      {metric.actual.toLocaleString()}
                      {metric.unit}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm" data-id="element-216">
                    <span className="text-muted-foreground" data-id="element-217">Abweichung:</span>
                    <span className={`font-medium flex items-center gap-1 ${positive ? 'text-green-600' : 'text-red-600'}`} data-id="element-218">
                      {positive ? <TrendingUp className="h-4 w-4" data-id="element-219" /> : <TrendingDown className="h-4 w-4" data-id="element-220" />}
                      {deviation}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-2" data-id="element-221">
                  <div className={`h-2 rounded-full ${positive ? 'bg-green-500' : 'bg-[#eb6641]'}`} style={{
                width: `${Math.min(metric.actual / metric.target * 100, 100)}%`
              }} data-id="element-222" />
                </div>
              </CardContent>
            </Card>;
      })}
      </div>

      <Card data-id="element-223">
        <CardHeader data-id="element-224">
          <CardTitle data-id="element-225">Kundenkennzahlen</CardTitle>
          <CardDescription data-id="element-226">
            Durchschnittliche Werte und Bindungsraten
          </CardDescription>
        </CardHeader>
        <CardContent data-id="element-227">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-228">
            <div className="space-y-2" data-id="element-229">
              <label className="text-sm font-medium" data-id="element-230">
                Durchschnittliche Abschlussgröße
              </label>
              <div className="space-y-2" data-id="element-231">
                <div className="flex items-center gap-2" data-id="element-232">
                  <span className="text-xs text-muted-foreground w-12" data-id="element-233">
                    Ziel:
                  </span>
                  <Input type="number" placeholder="5000" className="flex-1" data-id="element-234" />
                  <span className="text-sm" data-id="element-235">€</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-236">
                  <span className="text-xs text-muted-foreground w-12" data-id="element-237">
                    Ist:
                  </span>
                  <Input type="number" placeholder="4500" className="flex-1" data-id="element-238" />
                  <span className="text-sm" data-id="element-239">€</span>
                </div>
              </div>
            </div>
            <div className="space-y-2" data-id="element-240">
              <label className="text-sm font-medium" data-id="element-241">Kundenbindungsrate</label>
              <div className="space-y-2" data-id="element-242">
                <div className="flex items-center gap-2" data-id="element-243">
                  <span className="text-xs text-muted-foreground w-12" data-id="element-244">
                    Ziel:
                  </span>
                  <Input type="number" placeholder="85" className="flex-1" data-id="element-245" />
                  <span className="text-sm" data-id="element-246">%</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-247">
                  <span className="text-xs text-muted-foreground w-12" data-id="element-248">
                    Ist:
                  </span>
                  <Input type="number" placeholder="82" className="flex-1" data-id="element-249" />
                  <span className="text-sm" data-id="element-250">%</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="p-4 rounded-lg bg-[#eb6641]/10 border border-[#eb6641]/20" data-id="element-251">
        <p className="text-sm text-muted-foreground" data-id="element-252">
          💡 <strong data-id="element-253">Tipp:</strong> Aktualisieren Sie diese Übersicht monatlich,
          um die Vertriebsleistung zu überwachen und Verbesserungspotenziale zu
          identifizieren.
        </p>
      </div>
    </div>;
}